// ================================================================
// 公告橫幅組件
// ================================================================
// 版本：1.0
// 日期：2026-01-27
// 功能：在使用者頁面顯示系統公告
// ================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { Close, ExpandMore, ExpandLess } from '@mui/icons-material';
import { getActiveAnnouncements, Announcement } from '../../api/admin';

interface AnnouncementBannerProps {
  maxDisplay?: number; // 最多顯示幾則公告
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ maxDisplay = 3 }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const response = await getActiveAnnouncements();
        if (response.success && response.announcements) {
          setAnnouncements(response.announcements.slice(0, maxDisplay));
        }
      } catch (err) {
        console.error('Failed to load announcements:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnnouncements();
  }, [maxDisplay]);

  const handleDismiss = (id: number) => {
    setDismissedIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  };

  const handleToggleExpand = (id: number) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // 過濾已關閉的公告
  const visibleAnnouncements = announcements.filter(
    (a) => !dismissedIds.has(a.announcement_id)
  );

  if (isLoading || visibleAnnouncements.length === 0) {
    return null;
  }

  const getSeverity = (type: string): 'info' | 'warning' | 'success' | 'error' => {
    switch (type) {
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {visibleAnnouncements.map((announcement) => {
        const isExpanded = expanded.has(announcement.announcement_id);
        const isLongContent = announcement.content.length > 100;

        return (
          <Collapse key={announcement.announcement_id} in={!dismissedIds.has(announcement.announcement_id)}>
            <Alert
              severity={getSeverity(announcement.type)}
              sx={{ mb: 1 }}
              action={
                <Box display="flex" alignItems="center">
                  {isLongContent && (
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpand(announcement.announcement_id)}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDismiss(announcement.announcement_id)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <AlertTitle>{announcement.title}</AlertTitle>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                  overflow: isExpanded ? 'visible' : 'hidden',
                  textOverflow: isExpanded ? 'clip' : 'ellipsis',
                  maxWidth: isExpanded ? 'none' : '100%',
                }}
              >
                {isExpanded || !isLongContent
                  ? announcement.content
                  : `${announcement.content.substring(0, 100)}...`}
              </Typography>
            </Alert>
          </Collapse>
        );
      })}
    </Box>
  );
};

export default AnnouncementBanner;
